import { DialogProps } from '@/components/Dialog';
import { Recaptcha } from '@/components/Recaptcha';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { http } from '@/utils/http';
import { mergeOptions } from '@/utils/merge';
import { DefaultContactEditorialFormValidationSchema } from '@/utils/validations/contactEditorial';
import { zodResolver } from '@hookform/resolvers/zod';
import { getRecaptchaToken } from 'lib/services/recaptcha/getRecaptchaToken';
import { ReactNode, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ContactEditorial } from './ContactEditorial';

export interface StandaloneContactEditorial extends StandaloneComponentProps<typeof ContactEditorial> {
  headline?: ReactNode;
  caption?: ReactNode;
  label?: {
    email?: ReactNode;
    legal?: ReactNode;
    message?: ReactNode;
    name?: ReactNode;
    phone?: ReactNode;
  };
  submitButton?: ReactNode;
  successDialog?: DialogProps;
  errorDialog?: DialogProps;
}

type FormValues = z.infer<typeof DefaultContactEditorialFormValidationSchema>;

export const StandaloneContactEditorial: StandaloneComponent<StandaloneContactEditorial> = ({
  headline,
  caption,
  label,
  submitButton,
  successDialog,
  errorDialog,
  ...props
}) => {
  const [isSuccess, setIsSuccess] = useState(true);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const { register, handleSubmit, formState, reset } = useForm<FormValues>({
    resolver: zodResolver(DefaultContactEditorialFormValidationSchema),
  });

  const handleOpenModal = () => {
    dialogRef.current?.showModal();
  };

  const submitHandler = async (data: FormValues) => {
    const recaptchaToken = await getRecaptchaToken('contact_editorial');

    const result = await http.post('/api/contact-editorial', { ...data, recaptchaToken });

    setIsSuccess(Boolean(result));
    handleOpenModal();
    reset();
  };

  return (
    <ContactEditorial onSubmit={handleSubmit(submitHandler)} {...props} noValidate>
      <Recaptcha />
      <ContactEditorial.Headline>{headline}</ContactEditorial.Headline>
      <ContactEditorial.Caption>{caption}</ContactEditorial.Caption>
      <ContactEditorial.TextArea
        status={formState.errors.message ? 'error' : undefined}
        caption={formState.errors.message?.message}
        label={label?.message}
        options={mergeOptions({ $input: { ...register('message') } }, props?.options?.$textArea)}
      />
      <ContactEditorial.TextField
        status={formState.errors.name ? 'error' : undefined}
        caption={formState.errors.name?.message}
        label={label?.name}
        options={mergeOptions({ $input: { ...register('name') } }, props?.options?.$textField)}
      />
      <ContactEditorial.TextField
        status={formState.errors.phoneNumber ? 'error' : undefined}
        caption={formState.errors.phoneNumber?.message}
        label={label?.phone}
        type="tel"
        options={mergeOptions({ $input: { ...register('phoneNumber') } }, props?.options?.$textField)}
      />
      <ContactEditorial.TextField
        status={formState.errors.email ? 'error' : undefined}
        caption={formState.errors.email?.message}
        label={label?.email}
        type="email"
        options={mergeOptions({ $input: { ...register('email') } }, props?.options?.$textField)}
      />
      <ContactEditorial.SubmitButton
        options={mergeOptions(
          {
            type: 'submit' as const,
            disabled: formState.isLoading,
          },
          props.options?.$submitButton,
        )}
        content={submitButton}
      />
      <ContactEditorial.Checkbox
        label={label?.legal}
        options={mergeOptions({ $input: { ...register('legal') } }, props?.options?.$checkbox)}
        caption={formState.errors.legal?.message}
        status={formState.errors.legal ? 'error' : 'default'}
      />
      <ContactEditorial.Dialog options={{ ref: dialogRef }} {...(isSuccess ? successDialog : errorDialog)} />
    </ContactEditorial>
  );
};
